import { create } from "zustand";

export type ToastVariant = "success" | "info" | "error";

export interface Toast {
  id: number;
  message: string;
  variant: ToastVariant;
}

interface ToastState {
  toasts: Toast[];
  push(message: string, variant?: ToastVariant): number;
  dismiss(id: number): void;
}

let nextId = 1;

export const useToastStore = create<ToastState>()((set) => ({
  toasts: [],

  push(message: string, variant: ToastVariant = "success"): number {
    const id = nextId++;
    set((state) => ({ toasts: [...state.toasts, { id, message, variant }] }));
    return id;
  },

  dismiss(id: number): void {
    set((state) => ({
      toasts: state.toasts.filter((toast) => toast.id !== id),
    }));
  },
}));

/** Fires the success toast at the moment a lesson's goal is met. */
export function showLessonCompleteToast(): void {
  useToastStore.getState().push("Passed! Lesson complete.");
}

/** Turns any thrown value into a student-friendly message for an error toast. */
export function formatErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    const pythonLine = (error as Error & { line?: number }).line;
    if (error.name === "PythonSyntaxError" && typeof pythonLine === "number") {
      return `Python error (line ${pythonLine}): ${error.message}`;
    }
    const undefinedName = /^(\S+) is not defined$/.exec(error.message);
    if (error.name === "ReferenceError" && undefinedName) {
      return `Unknown command: ${undefinedName[1]}`;
    }
    return error.message;
  }
  return String(error);
}

/** Fires an error toast for a runtime/compile failure. */
export function showErrorToast(error: unknown): void {
  useToastStore.getState().push(formatErrorMessage(error), "error");
}
